import { motion } from "framer-motion";
import { Star, ArrowRight, Quote, MapPin, Clock, Utensils, Home as HomeIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Espacio, MenuItem, Testimonio, ROUTE_PATHS, formatPrice } from "@/lib/index";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { springPresets, hoverLift, fadeInUp } from "@/lib/motion";

/**
 * Tarjeta para los espacios principales del complejo (Restaurantes o Cabañas)
 */
export function EspacioCard({
  espacio
}: {
  espacio: Espacio;
}) {
  const Icon = espacio.nombre.includes("Cabañas") ? HomeIcon : Utensils;
  return <motion.div variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{
    once: true
  }} className="group relative h-[500px] overflow-hidden rounded-2xl shadow-lg">
      <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2031457394419482624_CABAÑA 7.jpeg?image_process=quality,q_90/resize,w_1280/format,webp" alt={espacio.nombre} className="absolute inset-0 w-full transition-transform duration-700 group-hover:scale-110 h-[605px] object-cover" />
      
      
      <div className="absolute bottom-0 p-8 text-white">
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Icon size={20} />
          </div>
          <Badge variant="outline" className="border-white/30 bg-white/10 text-white backdrop-blur-sm">
            {espacio.rangoPrecio}
          </Badge>
        </div>
        
        <h3 className="font-display text-3xl font-bold leading-tight mb-2">
          {espacio.nombre}
        </h3>
        <p className="text-white/80 line-clamp-2 mb-6 font-light italic">
          {espacio.tagline}
        </p>

        <Button asChild variant="secondary" className="group/btn bg-white text-primary hover:bg-accent hover:text-accent-foreground transition-all duration-300">
          <Link to={`${ROUTE_PATHS.ESPACIOS}#${espacio.slug}`} className="flex items-center gap-2">
            Explorar espacio
            <ArrowRight size={16} className="transition-transform group-hover/btn:translate-x-1" />
          </Link>
        </Button>
      </div>
    </motion.div>;
}

/**
 * Tarjeta para elementos del menú gastronómico
 */
export function MenuCard({
  item
}: {
  item: MenuItem;
}) {
  return <motion.div variants={hoverLift} initial="rest" whileHover="hover" className="h-full">
      <Card className="h-full flex flex-col overflow-hidden border-border bg-card shadow-sm transition-shadow hover:shadow-xl">
        <div className="relative h-56 overflow-hidden">
          <img src={item.imagen} alt={item.nombre} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          {item.esPopular && <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
              Favorito
            </Badge>}
          <Badge variant="secondary" className="absolute top-4 right-4 bg-background/80 backdrop-blur-md">
            {item.origen === "Gaviotas" ? "Las Gaviotas" : "Medusas"}
          </Badge>
        </div>

        <CardHeader className="p-5 pb-2">
          <div className="flex justify-between items-start gap-4">
            <h4 className="text-xl font-bold font-display text-foreground leading-tight">
              {item.nombre}
            </h4>
            <span className="font-mono text-primary font-semibold text-lg">
              {formatPrice(item.precio)}
            </span>
          </div>
        </CardHeader>

        <CardContent className="p-5 pt-0 flex-grow">
          <p className="text-muted-foreground text-sm leading-relaxed">
            {item.descripcion}
          </p>
        </CardContent>

        <CardFooter className="p-5 pt-0">
          <Badge variant="outline" className="capitalize text-[10px] tracking-widest">
            {item.categoria}
          </Badge>
        </CardFooter>
      </Card>
    </motion.div>;
}

/**
 * Tarjeta para testimonios de clientes
 */
export function TestimonioCard({
  testimonio
}: {
  testimonio: Testimonio;
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} transition={springPresets.gentle} viewport={{
    once: true
  }}>
      <Card className="relative h-full border-none bg-secondary/30 backdrop-blur-sm overflow-visible">
        <div className="absolute -top-4 -left-4 h-12 w-12 bg-accent flex items-center justify-center rounded-2xl shadow-md text-accent-foreground">
          <Quote size={24} />
        </div>

        <CardHeader className="pt-10 pb-4">
          <div className="flex gap-1 mb-2">
            {Array.from({
            length: 5
          }).map((_, i) => <Star key={i} size={16} className={i < testimonio.calificacion ? "fill-accent text-accent" : "text-muted"} />)}
          </div>
          <p className="text-lg font-medium italic text-foreground leading-relaxed">
            "{testimonio.comentario}"
          </p>
        </CardHeader>

        <CardContent className="flex items-center gap-4 border-t border-border/10 pt-6">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl border border-primary/20">
            {testimonio.nombre.charAt(0)}
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-foreground">{testimonio.nombre}</span>
            <span className="text-xs text-muted-foreground uppercase tracking-tighter">
              Visitó: {testimonio.servicioVisitado}
            </span>
          </div>
        </CardContent>

        <CardFooter className="justify-end pb-4">
          <span className="text-[10px] font-mono text-muted-foreground">
            {testimonio.fecha}
          </span>
        </CardFooter>
      </Card>
    </motion.div>;
}